/**
 * widget-contacts.ts — Kênh liên hệ ở màn chào widget Live Chat (merchant tự thêm, kiểu SaleSmartly).
 * Quy tắc kiểm tra KHỚP backend `modules/widget/widget-contacts.ts` (máy chủ vẫn kiểm lại khi lưu).
 */
export const CONTACT_TYPES = [
  'zalo', 'messenger', 'facebook', 'telegram', 'whatsapp', 'wechat', 'line',
  'tiktok', 'instagram', 'phone', 'email', 'website', 'help',
] as const
export type ContactType = (typeof CONTACT_TYPES)[number]

export interface ContactLink { type: ContactType; label: string; url: string }

export const MAX_CONTACTS = 10

export const CONTACT_META: Record<ContactType, { name: string; label: string; placeholder: string; logo?: string }> = {
  zalo: { name: 'Zalo', label: 'Chat qua Zalo', placeholder: 'https://zalo.me/0901234567', logo: '/brand-logos/zalo.svg' },
  messenger: { name: 'Messenger', label: 'Chat qua Messenger', placeholder: 'https://m.me/tenfanpage', logo: '/brand-logos/messenger.svg' },
  facebook: { name: 'Facebook', label: 'Fanpage Facebook', placeholder: 'https://facebook.com/tenfanpage', logo: '/brand-logos/facebook.svg' },
  telegram: { name: 'Telegram', label: 'Chat qua Telegram', placeholder: 'https://t.me/tenkenh', logo: '/brand-logos/telegram.svg' },
  whatsapp: { name: 'WhatsApp', label: 'Chat qua WhatsApp', placeholder: 'https://wa.me/84901234567', logo: '/brand-logos/whatsapp.svg' },
  wechat: { name: 'WeChat', label: 'Chat qua WeChat', placeholder: 'https://u.wechat.com/…', logo: '/brand-logos/wechat.svg' },
  line: { name: 'LINE', label: 'Chat qua LINE', placeholder: 'https://line.me/R/ti/p/@tenkenh', logo: '/brand-logos/line.svg' },
  tiktok: { name: 'TikTok', label: 'TikTok', placeholder: 'https://www.tiktok.com/@ten', logo: '/brand-logos/tiktok.svg' },
  instagram: { name: 'Instagram', label: 'Instagram', placeholder: 'https://instagram.com/ten', logo: '/brand-logos/instagram.svg' },
  phone: { name: 'Điện thoại', label: 'Gọi điện thoại', placeholder: '0901 234 567' },
  email: { name: 'Email', label: 'Gửi email', placeholder: 'cskh@congty.vn' },
  website: { name: 'Website', label: 'Website', placeholder: 'https://congty.vn' },
  help: { name: 'Trung tâm trợ giúp', label: 'Trung tâm trợ giúp', placeholder: 'https://congty.vn/tro-giup' },
}

const PHONE_RE = /^\+?[\d][\d\s.-]{5,19}$/
const EMAIL_RE = /^[^\s@<>"']+@[^\s@<>"']+\.[^\s@<>"']+$/

/** Lỗi của một dòng (chuỗi rỗng = hợp lệ). */
export function contactError(c: ContactLink): string {
  const v = c.url.trim()
  if (!v) return c.type === 'phone' ? 'Nhập số điện thoại' : c.type === 'email' ? 'Nhập email' : 'Nhập đường dẫn'
  if (v.length > 500) return 'Đường dẫn quá dài'
  if (c.type === 'phone') return PHONE_RE.test(v) ? '' : 'Số điện thoại không hợp lệ'
  if (c.type === 'email') return EMAIL_RE.test(v) ? '' : 'Email không hợp lệ'
  return /^https?:\/\/[^\s]+\.[^\s]+/i.test(v) ? '' : 'Đường dẫn phải bắt đầu bằng https://'
}

/** href dùng cho khung xem trước — cùng cách backend `publicContacts` tạo cho trang khách. */
export function contactHref(c: ContactLink): string {
  const v = c.url.trim()
  if (c.type === 'phone') return `tel:${v.replace(/[^\d+]/g, '')}`
  if (c.type === 'email') return `mailto:${v.toLowerCase()}`
  return v
}

/** Cấu hình gửi vào khung xem trước (cùng dạng `GET /api/v1/widget/:siteKey/config`). Bỏ dòng chưa hợp lệ. */
export function previewConfig(w: {
  title: string; displayName: string | null; logoUrl: string | null; greeting: string; primaryColor: string
  position: string; liveChatEnabled: boolean; homeTitle: string | null; contactLinks: ContactLink[]
}) {
  return {
    title: w.title,
    displayName: w.displayName?.trim() || w.title,
    logoUrl: w.logoUrl || null,
    greeting: w.greeting,
    primaryColor: w.primaryColor,
    position: w.position,
    homeTitle: w.homeTitle?.trim() || null,
    contacts: w.contactLinks.filter((c) => !contactError(c)).map((c) => ({
      type: c.type, label: c.label.trim() || CONTACT_META[c.type].label, href: contactHref(c),
    })),
    channels: { liveChat: w.liveChatEnabled },
  }
}
